import { Breadcrumbs as MuiBreadcrumbs } from "@mui/material";
import { Link } from "react-router";
import { Icon } from "@/components/icons";

export type BreadcrumbSegment = {
  label: string;
  href?: string;
  current?: boolean;
};

type BreadcrumbProps = {
  segments: BreadcrumbSegment[];
  /** Where the back arrow sends the user. Defaults to "/screening-portal". */
  backHref?: string;
};

/**
 * Brand breadcrumb — wraps `@mui/material/Breadcrumbs` with our slash separator
 * and brand link styling. The leading back-arrow link is rendered as a sibling
 * (not a breadcrumb item) to preserve the prior visual: tight gap-1, the arrow
 * uses `arrow_back` icon, separators are slim grey-300 slashes.
 */
export function Breadcrumb({ segments, backHref = "/screening-portal" }: BreadcrumbProps) {
  return (
    // min-w-0 lets the row shrink inside its parent (e.g. beside the Columns
    // button on L2); the back-arrow is shrink-0 so it never compresses.
    <div className="flex min-w-0 flex-1 items-center gap-1 text-body font-medium">
      <Link
        to={backHref}
        aria-label="Back"
        className="flex size-5 shrink-0 items-center justify-center text-grey-900 hover:text-black"
      >
        <Icon name="arrow_back" size={20} />
      </Link>
      <MuiBreadcrumbs
        aria-label="breadcrumb"
        separator={<span className="text-grey-300">/</span>}
        className="min-w-0 flex-1"
        sx={{
          minWidth: 0,
          // Clip an over-long trailing segment at the breadcrumb box so it can
          // never run past the viewport edge on a phone (belt-and-suspenders with
          // the per-<li> shrink below, which can't always ellipsize a 2-segment
          // crumb where there's no middle context segment to absorb the squeeze).
          overflow: "hidden",
          // Single line, never wrap: a long filename ellipsizes instead of
          // wrapping to 3 lines on phones (the longest segment shrinks first,
          // so the root and current name stay readable). Desktop is unaffected
          // because truncation only engages when space is actually tight.
          // width:100% bounds the (nowrap) list to the breadcrumbs box so the
          // shrinkable <li> actually shrinks + the leaf span ellipsizes, instead
          // of the list growing to content width and clipping at the screen edge.
          "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap", minWidth: 0, width: "100%", gap: "4px" },
          "& .MuiBreadcrumbs-li": {
            display: "inline-flex",
            alignItems: "center",
            minWidth: 0,
            overflow: "hidden",
          },
          // Shrink priority is set on the <li> (the flex item), not the span:
          // the root anchor stays fully readable (it's always a short fixed
          // label), the current/leaf resists, so the middle context segment
          // (e.g. a long filename) is what ellipsizes first.
          "& .MuiBreadcrumbs-li:first-of-type": { flexShrink: 0 },
          "& .MuiBreadcrumbs-li:last-of-type": { flexShrink: 0.4 },
          "& .MuiBreadcrumbs-separator": { margin: 0, flexShrink: 0 },
        }}
      >
        {segments.map((seg, i) =>
          seg.current || !seg.href ? (
            <span
              key={i}
              className={`block min-w-0 truncate ${seg.current ? "text-black" : "text-grey-500"}`}
              aria-current={seg.current ? "page" : undefined}
            >
              {seg.label}
            </span>
          ) : (
            <Link
              key={i}
              to={seg.href}
              className="block min-w-0 truncate text-grey-500 hover:text-grey-900"
            >
              {seg.label}
            </Link>
          ),
        )}
      </MuiBreadcrumbs>
    </div>
  );
}
